import { createFileRoute, useMatches } from "@tanstack/react-router";
import { z } from "zod";
import { NewTaskPage } from "@/components/campaign/new-task-page";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import { getWorkspaceCompanyProfileServer } from "@/server/workspace-company-profile";
import { getOnboardingWizardServer } from "@/server/onboarding";
import type { WorkspaceLayoutLoaderData } from "@/routes/workspace/$workspaceSlug/route";
import type { CampaignAgentMode } from "@/types/campaign";

function normalizeAgentMode(agent: string | undefined): CampaignAgentMode | undefined {
  if (agent === "sourcing" || agent === "sales") {
    return agent;
  }

  return undefined;
}

export const Route = createFileRoute("/workspace/$workspaceSlug/new-task")({
  validateSearch: z.object({
    prompt: z.string().optional(),
    agent: z.string().optional(),
  }),
  loader: async ({ context, parentMatchPromise }) => {
    const parentMatch = await parentMatchPromise;
    const workspaceId = await getWorkspaceIdWithFallback(context, "new-task", parentMatchPromise);
    const knowledgeProfile = await getWorkspaceCompanyProfileServer({
      data: { workspaceId },
    }).catch(() => null);
    const workspaceData = parentMatch.loaderData as WorkspaceLayoutLoaderData | undefined;
    const onboardingStatus = workspaceData?.onboardingStatus;
    const onboardingWizard =
      onboardingStatus &&
      (onboardingStatus.onboarding_flow_version ?? 1) >= 2 &&
      !onboardingStatus.onboarding_completed
        ? await getOnboardingWizardServer()
        : null;

    return {
      workspaceId,
      knowledgeProfile,
      onboardingWizard,
    };
  },
  component: NewTaskRoute,
});

function NewTaskRoute() {
  const { workspaceSlug } = Route.useParams();
  const search = Route.useSearch();
  const { workspaceId, knowledgeProfile, onboardingWizard } = Route.useLoaderData();
  const matches = useMatches();
  const workspaceMatch = matches.find((match) => match.routeId === "/workspace/$workspaceSlug");
  const workspaceData = workspaceMatch?.loaderData as WorkspaceLayoutLoaderData | undefined;
  const initialPrompt = search.prompt?.trim() ? search.prompt : undefined;
  const initialAgentMode = normalizeAgentMode(search.agent);

  return (
    <NewTaskPage
      workspaceId={workspaceId}
      workspaceSlug={workspaceSlug}
      knowledgeProfile={knowledgeProfile}
      billingSummary={workspaceData?.billingSummary}
      initialPrompt={initialPrompt}
      initialAgentMode={initialAgentMode}
      onboardingWizard={onboardingWizard}
    />
  );
}
